import React, { useEffect, useMemo, useRef, useState } from "react"
import Table from "../../components/Table"
import styles from "./transactions.module.css"
import api from "../../services/api"
import Input from "../../components/Form/Input"
import ModalDelete from "../../components/Modal/ModalDelete"
import { MdOutlineAccountBalanceWallet, MdOutlineDeleteOutline, MdOutlineMoneyOff, MdOutlinePersonOff, MdOutlineReceiptLong, MdOutlineTrendingDown, MdOutlineTrendingUp, MdOutlineWarningAmber } from "react-icons/md"
import { toast } from "react-toastify"
import Loading from "../layouts/Loading"
import { normalizeCpfCnpj, maskCurrency, maskDate, maskDocument } from "../../utils/formatter"
import { getOptionByOptionList, listMethodsPayment, openLinkExternal } from "../../utils/utils"
import ModalConfirm from "../../components/Modal/ModalConfirm"
import DatePicker from './../../components/Form/DatePicker/index'
import moment from "moment"
import Select from "../../components/Form/Select"
import Button from "../../components/Form/Button"
import BackgroundSelectFilter from "../../components/BackgroundSelectFilter"
import ModalFinancialEmail from "../../components/Modal/ModalTransactions/ModalFinancialEmail"
import ModalFinancialEmailHistory from "../../components/Modal/ModalTransactions/ModalFinancialEmailHistory"

const listTypes = [
    { id: "SUBSCRIPTION", value: "Mensalidade" },
    { id: "CUSTOM_SERVICE", value: "Serviço ou cobrança avulsa" },
    { id: "VOIP", value: "Créditos VOIP" },
    { id: "CHAT", value: "Créditos Chat" },
    { id: "PARTNER_PAYMENT", value: "Repasse mensal de parceiro" },
    { id: "ADDITIONAL_USERS", value: "Usuários adicionais" },
    { id: "ADDITIONAL_LEADS", value: "Base adicional de leads" },
    { id: "PLAN_CHANGE", value: "Troca de plano" },
]

const financialPaymentMethods = [
    ...listMethodsPayment,
    { id: "DEBIT_CARD", value: "Cartão de débito" },
    { id: "PIX", value: "PIX" },
    { id: "BANK_TRANSFER", value: "Transferência bancária" },
    { id: "CASH", value: "Dinheiro" },
    { id: "OTHER", value: "Outro meio" },
]

const listStatus = [
    { id: "PENDING", value: "Pendente" },
    { id: "ISSUED", value: "Em aberto" },
    { id: "APPROVED", value: "Aprovado" },
    { id: "RECUSED", value: "Recusado" },
    { id: "CANCELED", value: "Cancelado" },
]

const headers = [
    "#",
    "Documento",
    "Razão social",
    "Emissão",
    "Vencimento",
    "Pagamento",
    "Tipo",
    "Valor",
    "Desconto",
    "Total",
    "Método de pagamento",
    "NF",
    "Observação",
    "Status",
    ""
]

const TABLE_BATCH_SIZE = 80

const Transactions = () => {
    const [transactions, setTransactions] = useState([])
    const [summary, setSummary] = useState({})
    const [pagination, setPagination] = useState({ page: 1, limit: TABLE_BATCH_SIZE, total: 0, hasMore: false })
    const [filters, setFilters] = useState({})
    const [tempFilters, setTempFilters] = useState({})
    const [hasSearched, setHasSearched] = useState(false)
    const [modalDelete, setModalDelete] = useState(false)
    const [modalCancel, setModalCancel] = useState(false)
    const [transactionIdDelete, setTransactionIdDelete] = useState('')
    const [transactionIdCancel, setTransactionIdCancel] = useState('')
    const [emailAction, setEmailAction] = useState(null)
    const [emailHistory, setEmailHistory] = useState(null)
    const [loading, setLoading] = useState(false)
    const isLoadingTransactionRef = useRef(false)

    useEffect(() => {
        if (hasSearched) {
            getAllTransactions({ page: 1, reset: true })
        }
    }, [filters, hasSearched])

    const getTransactionParams = ({ page = 1, limit = TABLE_BATCH_SIZE, exportAll = false } = {}) => {
        const formatDate = (date) => date ? moment(date).format("YYYY-MM-DD") : undefined
        return {
            page,
            limit,
            exportAll: exportAll || undefined,
            dateStartEmit: formatDate(filters.dateStartEmit),
            dateEndEmit: formatDate(filters.dateEndEmit),
            dateStartDue: formatDate(filters.dateStartDue),
            dateEndDue: formatDate(filters.dateEndDue),
            dateStartPay: formatDate(filters.dateStartPay),
            dateEndPay: formatDate(filters.dateEndPay),
            type: filters?.type?.join?.(","),
            methodPayment: filters?.methodPayment?.join?.(","),
            status: filters?.status?.join?.(","),
            textSearch: filters?.textSearch || undefined,
        }
    }

    const getAllTransactions = async ({ page = 1, reset = false, limit = TABLE_BATCH_SIZE } = {}) => {
        try {
            if (!isLoadingTransactionRef.current) {
                isLoadingTransactionRef.current = true
                setLoading(true)
                const response = await api.get("/transactions", { params: getTransactionParams({ page, limit }) })
                const listTransactions = response.data?.data || []
                setTransactions(previousValue => reset ? listTransactions : [...previousValue, ...listTransactions])
                setSummary(response.data?.summary || {})
                setPagination(response.data?.pagination || { page, limit, total: listTransactions.length, hasMore: false })
            }
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setLoading(false)
            isLoadingTransactionRef.current = false
        }
    }

    const handleCancelTransaction = (id) => {
        setTransactionIdCancel(id)
        setModalCancel(true)
    }

    const closeModalCancel = () => {
        setModalCancel(false)
    }

    const handleDeleteTransaction = (id) => {
        setTransactionIdDelete(id)
        setModalDelete(true)
    }

    const handleEmitCharge = (id) => {
        setLoading(true)
        const account_id = getAccountIdByTransaction(id)
        api.post(`/accounts/${account_id}/transactions/${id}/charge/emit`).then(response => {
            const newTransaction = response?.data?.data
            updateTransaction(newTransaction)
            toast.success(response?.data?.message)
        }).catch(err => toast.error(err?.response?.data?.message)).finally(() => setLoading(false))
    }

    const handleSendCharge = (id) => {
        const account_id = getAccountIdByTransaction(id)
        setEmailAction({ transactionId: id, accountId: account_id, documentType: "charge" })
    }

    const handleEmitInvoice = (id) => {
        setLoading(true)
        const account_id = getAccountIdByTransaction(id)
        api.post(`/accounts/${account_id}/transactions/${id}/invoice/emit`).then(response => {
            const newTransaction = response?.data?.data
            updateTransaction(newTransaction)
            toast.success(response?.data?.message)
        }).catch(err => toast.error(err?.response?.data?.message)).finally(() => setLoading(false))
    }

    const handleSendInvoice = (id) => {
        const account_id = getAccountIdByTransaction(id)
        setEmailAction({ transactionId: id, accountId: account_id, documentType: "invoice" })
    }

    const sendFinancialEmail = recipients => {
        if (!emailAction) return
        setLoading(true)
        api.post(`/accounts/${emailAction.accountId}/transactions/${emailAction.transactionId}/${emailAction.documentType}/send`, { recipients }).then(response => {
            updateTransaction(response?.data?.data)
            response.data?.warning ? toast.warning(response.data.message) : toast.success(response.data.message)
            setEmailAction(null)
        }).catch(err => toast.error(err?.response?.data?.message || "Não foi possível enviar o documento."))
            .finally(() => setLoading(false))
    }

    const updateTransaction = (newTransaction) => {
        setTransactions(previousValue => (
            previousValue.map(transaction => {
                if (transaction?._id === newTransaction?._id) {
                    return newTransaction
                }
                return transaction
            })
        ))
    }

    const closeModalDelete = () => {
        setModalDelete(false)
    }

    const getAccountIdByTransaction = (id) => {
        const transaction = transactions.find(transaction => transaction._id === id)
        return transaction?.account_id
    }

    const deleteTransaction = (id) => {
        setLoading(true)
        const account_id = getAccountIdByTransaction(id)
        api.delete(`/accounts/${account_id}/transactions/${id}`).then(response => {
            setTransactions(previousValue => previousValue?.filter(transaction => transaction._id !== id))
            toast.success(response.data.message)
            closeModalDelete()
        }).catch(err => toast.error(err?.response?.data?.message)).finally(() => setLoading(false))
    }

    const cancelTransaction = () => {
        setLoading(true)
        const account_id = getAccountIdByTransaction(transactionIdCancel)
        api.patch(`/accounts/${account_id}/transactions/${transactionIdCancel}/cancel`).then(response => {
            const dataUpdate = response?.data?.data
            setTransactions(previousValue => {
                return previousValue.map(transaction => {
                    if (transaction._id === transactionIdCancel) {
                        return { ...transaction, ...dataUpdate }
                    }
                    return transaction
                })

            })
            toast.success(response.data.message)
            closeModalCancel()
        }).catch(err => toast.error(err?.response?.data?.message)).finally(() => setLoading(false))
    }

    const getDataNewTransaction = (newTransaction) => {
        const actions = []
        if (newTransaction?.charge_status === "OPENED" && newTransaction?.link_charge) {
            actions.push({
                label: "Abrir cobrança",
                onClick: () => openLinkExternal(newTransaction.link_charge)
            })
            actions.push({
                label: "Enviar cobrança",
                onClick: handleSendCharge
            })
        } else if (newTransaction?.charge_status === "PENDING") {
            actions.push({
                label: "Emitir cobrança",
                onClick: handleEmitCharge
            })
        }
        if (newTransaction?.link_invoice) {
            actions.push({
                label: "Abrir Nota Fiscal",
                onClick: () => openLinkExternal(newTransaction.link_invoice)
            })
            actions.push({
                label: "Enviar Nota Fiscal",
                onClick: handleSendInvoice
            })
        } else if (newTransaction?.status !== "CANCELED") {
            actions.push({
                label: "Emitir Nota Fiscal",
                onClick: handleEmitInvoice
            })
        }
        if (newTransaction?.metadata?.email_delivery_ids?.length) {
            actions.push({
                label: "Histórico de envios",
                onClick: id => setEmailHistory({ transactionId: id, accountId: getAccountIdByTransaction(id) })
            })
        }
        if (newTransaction?.status !== "APPROVED") {
            if (newTransaction?.status !== "CANCELED") {
                actions.push({
                    label: "Cancelar cobrança",
                    onClick: handleCancelTransaction
                })
            }
            actions.push({
                label: "Excluir registro",
                onClick: handleDeleteTransaction
            })
        }
        const emit_date = newTransaction?.emit_date || newTransaction?.created_at
        const due_date = newTransaction?.due_date || newTransaction?.created_at
        const payday = newTransaction?.payday ? newTransaction?.payday : (newTransaction?.status === "APPROVED" ? newTransaction?.created_at : "")
        return {
            id: newTransaction._id,
            name: newTransaction.name,
            emit_date: emit_date,
            due_date: due_date,
            payday: payday,
            type: newTransaction?.type,
            method: newTransaction?.method,
            status: newTransaction?.status,
            data: newTransaction,
            value: [
                maskDocument(newTransaction?.account_document) || "-",
                newTransaction?.account_name || "-",
                emit_date ? maskDate(emit_date) : "-",
                due_date ? maskDate(due_date) : "-",
                payday ? maskDate(payday) : "-",
                getOptionByOptionList(newTransaction.type, listTypes) || "-",
                maskCurrency(parseFloat(newTransaction.value).toFixed(2)) || "-",
                maskCurrency(parseFloat(newTransaction.discount).toFixed(2)) || "-",
                maskCurrency(parseFloat(newTransaction.value - newTransaction.discount).toFixed(2)) || "-",
                getOptionByOptionList(newTransaction.method, financialPaymentMethods) || "-",
                newTransaction?.number_nf || "-",
                newTransaction?.observation?.substring(0, 20) || "-",
                getOptionByOptionList(newTransaction.status, listStatus) || "-",
            ],
            actions
        }
    }

    const filteredValues = useMemo(() => {
        return transactions.map((transaction) => getDataNewTransaction(transaction))
    }, [transactions])

    const loadMoreRows = () => {
        if (pagination.hasMore && !isLoadingTransactionRef.current) {
            getAllTransactions({ page: pagination.page + 1 })
        }
    }

    const financialSummary = useMemo(() => {
        return {
            ...summary,
            churnAccounts: { length: summary?.churnCount || 0 },
            churnRiskAccounts: { length: summary?.churnRiskCount || 0 },
            byType: (summary?.byType || []).map(type => ({
                ...type,
                label: getOptionByOptionList(type.id, listTypes) || type.id,
            })),
        }
    }, [summary])

    const handleChangeFilters = (name, value) => {
        setTempFilters(previousValue => ({ ...previousValue, [name]: value }))
    }

    const handleChangePeriod = (dateStart, dateEnd, prefix) => {
        handleChangeFilters(`dateStart${prefix}`, dateStart)
        handleChangeFilters(`dateEnd${prefix}`, dateEnd)
    }

    const applyFilters = () => {
        setHasSearched(true)
        setFilters({ ...tempFilters })
    }

    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            applyFilters()
        }
    }

    const getTransactionsForExport = async () => {
        const response = await api.get("/transactions", { params: getTransactionParams({ exportAll: true }) })
        return (response.data?.data || [])
    }

    const generateAccountingFile = async () => {
        try {
            setLoading(true)
            const transactionsToExport = await getTransactionsForExport()
            let fileContent = ""
            transactionsToExport.filter(transaction => transaction?.value - transaction?.discount > 0).forEach((transaction, index) => {
                const paidAmount = transaction.value
                const discount = transaction.discount || 0
                const accountingAmount = paidAmount - discount
                const receivedAmount = paidAmount
                const interest = discount

                const invoiceNumber = transaction.number_nf
                const accountingAccount = transaction.accounting_account || "14"

                fileContent += formatTransactionLine(transaction, receivedAmount, invoiceNumber, index, "D", accountingAccount)
                fileContent += formatTransactionLine(transaction, accountingAmount, invoiceNumber, index, "C")

                if (interest > 0) {
                    fileContent += formatTransactionLine(transaction, interest, invoiceNumber, index, "C", "935", true)
                }
            })

            const blob = new Blob([fileContent], { type: "text/plain;charset=utf-8" })
            const fileName = `Entradas_${new Date().toISOString().replace(/[:.]/g, "-")}.txt`

            const link = document.createElement("a")
            link.href = URL.createObjectURL(blob)
            link.download = fileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível exportar as transações.")
        } finally {
            setLoading(false)
        }
    }

    const formatTransactionLine = (transaction, amount, invoiceNumber, groupKey, type, accountingAccount = "", isInterest = false) => {
        const prefix = "Recebimento"
        const historyPrefix = isInterest
            ? `${prefix} de Juros NF ${invoiceNumber}`
            : `${prefix} NF ${invoiceNumber}`

        const date = moment(transaction.payday || transaction.created_at).format("DDMMYYYY")

        const clientName = transaction.account_name || "Cliente"

        const record = [
            "00", // TPREGISTRO
            "153",   // EMPRESA
            "1",   // FILIAL
            date, // DATA
            transaction._id.toString(), // NRLCTOERP
            type, // D ou C
            accountingAccount, // CONTA
            "",   // SUBCONTA
            amount.toFixed(2).replace(".", ","), // VALOR
            "0",  // AÇÃO
            "2",  // PRIMEIROHISTCTA
            "",   // CODHISTORICO
            `"${(historyPrefix + " " + clientName).substring(0, 150)}"`, // COMPLHISTORICO
            groupKey.toString(), // GRUPOLCTO
            normalizeCpfCnpj(transaction.account_document), // CNPJ
            "", // IESTADUAL
            "1", // TPCNPJ
            "", // CONTAORIGEM
            "52068149000106", // CNPJEMPRESA
            "", // IEEMPRESA
        ]

        return record.join(";") + "\n"
    }

    const formatMoney = (value = 0) => {
        return maskCurrency(Number(value || 0).toFixed(2))
    }

    const formatPercentage = (value = 0) => {
        return `${Number(value || 0).toFixed(1).replace(".", ",")}%`
    }

    return (
        <div className={styles.container}>
            <section className={styles.summaryGrid}>
                <div className={`${styles.summaryCard} ${styles.revenue}`}>
                    <div className={styles.summaryIcon}><MdOutlineTrendingUp /></div>
                    <small>Faturamento recebido</small>
                    <strong>{formatMoney(financialSummary.revenue)}</strong>
                    <span>{financialSummary.approvedCount} cobrança{financialSummary.approvedCount === 1 ? "" : "s"} aprovada{financialSummary.approvedCount === 1 ? "" : "s"}</span>
                </div>
                <div className={`${styles.summaryCard} ${styles.open}`}>
                    <div className={styles.summaryIcon}><MdOutlineReceiptLong /></div>
                    <small>Em aberto</small>
                    <strong>{formatMoney(financialSummary.openValue)}</strong>
                    <span>{financialSummary.openedCount} pendente{financialSummary.openedCount === 1 ? "" : "s"} ou em aberto</span>
                </div>
                <div className={`${styles.summaryCard} ${styles.loss}`}>
                    <div className={styles.summaryIcon}><MdOutlineTrendingDown /></div>
                    <small>Perdas</small>
                    <strong>{formatMoney(financialSummary.lossValue)}</strong>
                    <span>{financialSummary.lossesCount} recusada{financialSummary.lossesCount === 1 ? "" : "s"} ou cancelada{financialSummary.lossesCount === 1 ? "" : "s"}</span>
                </div>
                <div className={`${styles.summaryCard} ${styles.overdue}`}>
                    <div className={styles.summaryIcon}><MdOutlineWarningAmber /></div>
                    <small>Vencidas</small>
                    <strong>{formatMoney(financialSummary.overdueValue)}</strong>
                    <span>{financialSummary.overdueCount} cobrança{financialSummary.overdueCount === 1 ? "" : "s"} atrasada{financialSummary.overdueCount === 1 ? "" : "s"}</span>
                </div>
            </section>
            <section className={styles.indicators}>
                <div className={styles.indicatorCard}>
                    <small>Receita no mês</small>
                    <strong>{formatMoney(financialSummary.currentMonthRevenue)}</strong>
                    <span className={financialSummary.monthVariation >= 0 ? styles.positive : styles.negative}>
                        {financialSummary.monthVariation >= 0 ? <MdOutlineTrendingUp /> : <MdOutlineTrendingDown />}
                        {formatPercentage(financialSummary.monthVariation)} vs. mês anterior
                    </span>
                </div>
                <div className={styles.indicatorCard}>
                    <small>Ticket médio aprovado</small>
                    <strong>{formatMoney(financialSummary.averageTicket)}</strong>
                    <span><MdOutlineAccountBalanceWallet /> Baseado em cobranças aprovadas</span>
                </div>
                <div className={styles.indicatorCard}>
                    <small>Descontos concedidos</small>
                    <strong>{formatMoney(financialSummary.totalDiscount)}</strong>
                    <span><MdOutlineMoneyOff /> Sobre {formatMoney(financialSummary.totalValue)} bruto</span>
                </div>
                <div className={styles.indicatorCard}>
                    <small>Taxa de aprovação</small>
                    <strong>{formatPercentage(financialSummary.approvalRate)}</strong>
                    <span>{financialSummary.totalCount} registro{financialSummary.totalCount === 1 ? "" : "s"} no filtro atual</span>
                </div>
                <div className={`${styles.indicatorCard} ${financialSummary.churnAccounts.length > 0 ? styles.danger : ""}`}>
                    <small>Churn identificado</small>
                    <strong>{financialSummary.churnAccounts.length}</strong>
                    <span><MdOutlinePersonOff /> Cliente{financialSummary.churnAccounts.length === 1 ? "" : "s"} direto{financialSummary.churnAccounts.length === 1 ? "" : "s"} sem renovação</span>
                </div>
                <div className={`${styles.indicatorCard} ${financialSummary.churnRiskAccounts.length > 0 ? styles.warning : ""}`}>
                    <small>Risco de churn</small>
                    <strong>{financialSummary.churnRiskAccounts.length}</strong>
                    <span><MdOutlineWarningAmber /> Expirado com cobrança ou renovação desligada</span>
                </div>
            </section>
            {financialSummary.byType.length > 0 &&
                <section className={styles.typeBreakdown}>
                    <div className={styles.sectionTitle}>
                        <strong>Distribuição por tipo</strong>
                        <span>Valores líquidos no filtro atual</span>
                    </div>
                    <div className={styles.typeList}>
                        {financialSummary.byType.map(type => (
                            <div className={styles.typeItem} key={type.id}>
                                <div>
                                    <strong>{type.label}</strong>
                                    <small>{type.count} registro{type.count === 1 ? "" : "s"}</small>
                                </div>
                                <span>{formatMoney(type.amount)}</span>
                            </div>
                        ))}
                    </div>
                </section>
            }
            <div className={styles.actions}>
                <DatePicker name="Data de Emissão" dateStart={tempFilters.dateStartEmit || ""} dateEnd={tempFilters.dateEndEmit || ""} onChange={(dateStart, dateEnd) => handleChangePeriod(dateStart, dateEnd, "Emit")} multiple={true} />
                <DatePicker name="Data de Vencimento" dateStart={tempFilters.dateStartDue || ""} dateEnd={tempFilters.dateEndDue || ""} onChange={(dateStart, dateEnd) => handleChangePeriod(dateStart, dateEnd, "Due")} multiple={true} />
                <DatePicker name="Data de Pagamento" dateStart={tempFilters.dateStartPay || ""} dateEnd={tempFilters.dateEndPay || ""} onChange={(dateStart, dateEnd) => handleChangePeriod(dateStart, dateEnd, "Pay")} multiple={true} />
                <Select multiple={true} name="Tipo" placeholder="Buscar..." options={listTypes} onChange={(value) => handleChangeFilters("type", value)} selected={tempFilters?.type || []} />
                <Select multiple={true} name="Método de Pagamento" placeholder="Buscar..." options={financialPaymentMethods} onChange={(value) => handleChangeFilters("methodPayment", value)} selected={tempFilters?.methodPayment || []} />
                <Select multiple={true} name="Status" placeholder="Buscar..." options={listStatus} onChange={(value) => handleChangeFilters("status", value)} selected={tempFilters?.status || []} />
                <Input type="search" placeholder="Buscar..." value={tempFilters.textSearch || ""} onChange={(e) => handleChangeFilters("textSearch", e.target.value)} onKeyDown={handleSearchKeyDown} />
                <Button variant="secondary" onClick={applyFilters}>Filtrar</Button>
            </div>
            <div className={styles.buttons}>
                <Button variant="secondary" onClick={generateAccountingFile}>Exportar para TXT</Button>
            </div>
            <div className={styles.resultsArea}>
                {hasSearched ?
                    <Table
                        headers={headers}
                        values={filteredValues}
                        className={`${styles.transactionsTable} ${filteredValues.length >= 10 ? styles.transactionsTableWithTenRows : ""}`}
                        loadMore={pagination.hasMore}
                        onScroll={loadMoreRows}
                        messageNotRegisters="Nenhuma cobrança encontrada"
                        showIndex={true}
                        showCount={true}
                        count={`${filteredValues.length}/${pagination.total || 0}`}
                    />
                    :
                    <BackgroundSelectFilter
                        title="Filtre antes de carregar"
                        text="Escolha o período e os demais filtros para buscar somente as cobranças que você precisa visualizar."
                    />
                }
            </div>
            {modalCancel &&
                <ModalConfirm
                    closeModal={closeModalCancel}
                    onSubmit={cancelTransaction}
                    icon={<MdOutlineDeleteOutline />}
                    title="Cancelamento de cobrança"
                    description="Deseja realmente cancelar a cobrança?"
                    text="Ao cancelar a cobrança, não será mais possível realizar o pagamento. Para isso será necessário criar uma nova cobrança!"
                />
            }
            {modalDelete &&
                <ModalDelete
                    closeModal={closeModalDelete}
                    onSubmit={deleteTransaction}
                    icon={<MdOutlineDeleteOutline />}
                    title="Exclusão de registro"
                    description="Deseja realmente excluir o registro?"
                    id={transactionIdDelete}
                />
            }
            {emailAction && <ModalFinancialEmail accountId={emailAction.accountId} documentType={emailAction.documentType} onSubmit={sendFinancialEmail} closeModal={() => setEmailAction(null)} />}
            {emailHistory && <ModalFinancialEmailHistory accountId={emailHistory.accountId} transactionId={emailHistory.transactionId} closeModal={() => setEmailHistory(null)} />}
            {loading && <Loading fullPage={true} />}
        </div>
    )
}

export default Transactions
